import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Alert,
  Modal,
  Button,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import {
  Send as SendIcon,
  Home as HomeIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useMaster } from '../hooks/useMaster';
import { Message } from '../types/message';
import MessageList from './MessageList';
import AudioRecorder from './AudioRecorder';
import { config } from '../config';

const ChatInterface: React.FC = () => {
  const { user, isGuestMode, logout, refreshUser } = useAuth();
  const { socket } = useSocket();
  const { master, personality } = useMaster();
  const accentColor = personality?.colors?.headerStart || '#8B5CF6';
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyChecked, setHistoryChecked] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [communicationError, setCommunicationError] = useState(false);
  
  // Estados para el modal de configuración
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsName, setSettingsName] = useState('');
  const [settingsPassword, setSettingsPassword] = useState('');
  const [settingsConfirmPassword, setSettingsConfirmPassword] = useState('');
  const [showSettingsPassword, setShowSettingsPassword] = useState(false);
  const [showSettingsConfirmPassword, setShowSettingsConfirmPassword] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsSuccess, setSettingsSuccess] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const premiumTypingDelayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const premiumTypingHideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const premiumTypingEndRef = useRef<number>(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const messagesRef = useRef<Message[]>([]);
  const handleRecorderError = useCallback((message: string) => {
    setError(message);
  }, []);
  const clearPremiumTypingTimers = useCallback(() => {
    if (premiumTypingDelayTimeoutRef.current) {
      clearTimeout(premiumTypingDelayTimeoutRef.current);
      premiumTypingDelayTimeoutRef.current = null;
    }
    if (premiumTypingHideTimeoutRef.current) {
      clearTimeout(premiumTypingHideTimeoutRef.current);
      premiumTypingHideTimeoutRef.current = null;
    }
    premiumTypingEndRef.current = 0;
  }, []);

  const stopTypingIndicator = useCallback(() => {
    const remaining =
      premiumTypingEndRef.current > 0
        ? Math.max(0, premiumTypingEndRef.current - Date.now())
        : 0;

    if (premiumTypingDelayTimeoutRef.current) {
      clearTimeout(premiumTypingDelayTimeoutRef.current);
      premiumTypingDelayTimeoutRef.current = null;
    }

    if (premiumTypingHideTimeoutRef.current) {
      clearTimeout(premiumTypingHideTimeoutRef.current);
      premiumTypingHideTimeoutRef.current = null;
    }

    if (remaining > 0) {
      premiumTypingHideTimeoutRef.current = setTimeout(() => {
        premiumTypingHideTimeoutRef.current = null;
        setIsBotTyping(false);
      }, remaining);
    } else {
      setIsBotTyping(false);
    }

    premiumTypingEndRef.current = 0;
  }, []);

  const startTypingIndicators = useCallback((initialDelay: number = 2000) => {
    const hasPendingMessage = messagesRef.current.some(
      (msg) => msg.sender === 'user' && msg.status === 'sending'
    );
    if (!hasPendingMessage) {
      return;
    }

    clearPremiumTypingTimers();

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    if ((typingTimeoutRef as any).typingDelayTimeout) {
      clearTimeout((typingTimeoutRef as any).typingDelayTimeout);
      (typingTimeoutRef as any).typingDelayTimeout = null;
    }

    if (initialDelay <= 0) {
      setIsBotTyping(true);
    } else {
      (typingTimeoutRef as any).typingDelayTimeout = setTimeout(() => {
        setIsBotTyping(true);
      }, initialDelay);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setIsBotTyping(false);
      setCommunicationError(true);
      typingTimeoutRef.current = null;
      if ((typingTimeoutRef as any).typingDelayTimeout) {
        clearTimeout((typingTimeoutRef as any).typingDelayTimeout);
        (typingTimeoutRef as any).typingDelayTimeout = null;
      }
    }, 60000);
  }, [clearPremiumTypingTimers]);
  const handleSubscriptionRestriction = useCallback((message?: string) => {
    const finalMessage = message || 'Tu membresía no está activa. Adquiere la membresía para continuar.';
    setIsPremium(false);
    setError(finalMessage);
    setIsBotTyping(false);
    setCommunicationError(false);
    clearPremiumTypingTimers();
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    if ((typingTimeoutRef as any).typingDelayTimeout) {
      clearTimeout((typingTimeoutRef as any).typingDelayTimeout);
      (typingTimeoutRef as any).typingDelayTimeout = null;
    }
    setMessages(prev => {
      const updated = [...prev];
      for (let i = updated.length - 1; i >= 0; i--) {
        if (updated[i].sender === 'user' && updated[i].status === 'sending') {
          updated[i] = { ...updated[i], status: 'error' };
          break;
        }
      }
      return updated;
    });
  }, [clearPremiumTypingTimers]);

  const handleNavigateHome = () => {
    window.location.href = config.HOME_URL;
  };

  const handleLogout = () => {
    logout();
  };

  const handleOpenSettings = async () => {
    // Obtener el usuario más reciente del backend antes de abrir el modal
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Agregar timestamp para evitar caché
        const timestamp = Date.now();
        const response = await fetch(`${config.API_URL}/api/auth/verify?t=${timestamp}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0',
          },
          cache: 'no-store',
        });
        if (response.ok) {
          const data = await response.json();
          if (data.valid && data.user) {
            setSettingsName(data.user.name || '');
            // Abrir modal después de obtener el nombre actualizado
            setSettingsPassword('');
            setSettingsConfirmPassword('');
            setSettingsError(null);
            setSettingsSuccess(false);
            setSettingsModalOpen(true);
            return;
          }
        }
      }
      // Si falla, usar el nombre del contexto y abrir modal
      setSettingsName(user?.name || '');
      setSettingsPassword('');
      setSettingsConfirmPassword('');
      setSettingsError(null);
      setSettingsSuccess(false);
      setSettingsModalOpen(true);
    } catch (error) {
      console.error('Error al obtener usuario:', error);
      // Si hay error, usar el nombre del contexto y abrir modal
      setSettingsName(user?.name || '');
      setSettingsPassword('');
      setSettingsConfirmPassword('');
      setSettingsError(null);
      setSettingsSuccess(false);
      setSettingsModalOpen(true);
    }
  };

  const handleCloseSettings = () => {
    setSettingsModalOpen(false);
    setSettingsName('');
    setSettingsPassword('');
    setSettingsConfirmPassword('');
    setSettingsError(null);
    setSettingsSuccess(false);
  };

  const handleUpdateProfile = async () => {
    setSettingsLoading(true);
    setSettingsError(null);
    setSettingsSuccess(false);

    try {
      // Validar que el nombre no esté vacío
      if (!settingsName || settingsName.trim() === '') {
        setSettingsError('El nombre es requerido');
        setSettingsLoading(false);
        return;
      }

      // Si se proporciona contraseña, validar que coincidan
      if (settingsPassword) {
        if (settingsPassword.length < 6) {
          setSettingsError('La contraseña debe tener al menos 6 caracteres');
          setSettingsLoading(false);
          return;
        }
        if (settingsPassword !== settingsConfirmPassword) {
          setSettingsError('Las contraseñas no coinciden');
          setSettingsLoading(false);
          return;
        }
      }

      // Preparar datos para actualizar
      const updateData: { name: string; password?: string } = {
        name: settingsName.trim(),
      };

      // Solo incluir password si se proporcionó
      if (settingsPassword) {
        updateData.password = settingsPassword;
      }

      // Llamar al endpoint de actualización
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al actualizar el perfil');
      }

      // El backend devuelve el usuario actualizado
      setSettingsSuccess(true);
      
      // Actualizar el contexto inmediatamente para que el avatar y otros componentes se actualicen
      await refreshUser();
      
      // Cerrar el modal después de mostrar el mensaje de éxito
      setTimeout(() => {
        handleCloseSettings();
      }, 1500);
    } catch (err: any) {
      setSettingsError(err.message || 'Error al actualizar el perfil');
    } finally {
      setSettingsLoading(false);
    }
  };

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Cleanup timeout al desmontar componente
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if ((typingTimeoutRef as any).typingDelayTimeout) {
        clearTimeout((typingTimeoutRef as any).typingDelayTimeout);
      }
      if (premiumTypingDelayTimeoutRef.current) {
        clearTimeout(premiumTypingDelayTimeoutRef.current);
      }
      if (premiumTypingHideTimeoutRef.current) {
        clearTimeout(premiumTypingHideTimeoutRef.current);
      }
      premiumTypingEndRef.current = 0;
    };
  }, []);

  // Cargar historial automáticamente si el usuario abre la app y hay mensajes en BD (solo si no es guest)
  // También recargar cuando cambie el master para mostrar solo las conversaciones de ese bot
  useEffect(() => {
    const loadHistoryIfNeeded = async () => {
      if (!user?.id || isGuestMode) return;
      
      try {
        // Incluir master en la URL para filtrar conversaciones por bot
        const masterParam = master ? `&master=${encodeURIComponent(master)}` : '';
        const response = await fetch(`${config.API_URL}/api/chat/history/${user.id}?channel=webapp&limit=10${masterParam}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          },
          cache: 'no-store'
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // Si hay mensajes en BD, cargar el historial
          if (data.success && data.messages && data.messages.length > 0) {
            setMessages(data.messages);
          } else {
            // Si no hay mensajes, limpiar el estado
            setMessages([]);
          }
        } else {
          console.error('Error al cargar historial:', response.status);
        }
        
        setHistoryChecked(true);
      } catch (error) {
        console.error('Error al verificar historial:', error);
        setHistoryChecked(true);
      }
    };

    // Cargar historial después de un pequeño delay para asegurar que el componente esté montado
    // Para guests, marcar como checked inmediatamente sin intentar cargar
    if (isGuestMode) {
      setHistoryChecked(true);
      return;
    }
    
    // Resetear historyChecked cuando cambie el master para forzar recarga
    setHistoryChecked(false);
    const timer = setTimeout(loadHistoryIfNeeded, 1000);
    return () => clearTimeout(timer);
  }, [user?.id, isGuestMode, master]);

  // Cargar estado de suscripción del usuario (solo si no es guest)
  useEffect(() => {
    const loadSubscriptionStatus = async () => {
      if (!user?.id || isGuestMode) return;
      
      try {
        const response = await fetch(`${config.API_URL}/api/chat/subscription-status/${user.id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          },
          cache: 'no-store'
        });
        
        if (response.ok) {
          const data = await response.json();
          const reason = data.reason || null;
          const premiumActive = reason
            ? reason.toLowerCase().includes('suscripción válida')
            : Boolean(data.isPremium);
          setIsPremium(premiumActive);
        }
      } catch (error) {
        console.error('Error al verificar estado de suscripción:', error);
        setIsPremium(false);
      }
    };

    loadSubscriptionStatus();
  }, [user?.id, isGuestMode]);

  // Mensaje automático del bot en modo guest (solo si no hay historial)
  useEffect(() => {
    if (isGuestMode && messages.length === 0 && historyChecked && !isBotTyping) {
      // Mensaje de bienvenida dinámico según la personalidad
      let welcomeMessageContent = '';
      
      switch (master) {
        case 'luna':
          welcomeMessageContent = `¡Hola! Soy Luna Celestial, especialista en magia lunar y nuevos comienzos. ¿Buscas un ritual para atraer abundancia, saber si es el momento ideal para emprender, o si debes cerrar un ciclo y renacer con nuevas energías? ¿En qué puedo ayudarte hoy?`;
          break;
        case 'oro':
          welcomeMessageContent = `¡Bienvenido! Soy Oro Místico, guía en energías de prosperidad, éxito y propósito. Si buscas activar tu abundancia, equilibrar tus finanzas espirituales, o descubrir cómo atraer oportunidades a tu vida, estás en el lugar correcto. Cuéntame, ¿qué deseas manifestar hoy?`;
          break;
        case 'rosa':
          welcomeMessageContent = `¡Hola! Soy Rosa Encantada, maestra del tarot de vínculos y conexiones del alma. Si buscas entender qué te enseña tu relación actual, sanar una conexión que duele o saber si estás alineado emocionalmente con alguien, estoy aquí para ayudarte a encontrar claridad y equilibrio en tus lazos más profundos. ¿Qué vínculo te gustaría explorar hoy?`;
          break;
        case 'sage':
          welcomeMessageContent = `¡Hola! Soy Sage Universal, orientador en energía, equilibrio interior y sanación emocional. Si sientes cansancio, bloqueo o desconexión, puedo ayudarte a armonizar tus chakras, restablecer tu energía y recuperar tu centro espiritual. Dime, ¿cómo te sientes hoy y en qué área quieres encontrar equilibrio?`;
          break;
        case 'mystic':
          welcomeMessageContent = `¡Hola! soy Mystic Oracle, intérprete de numerología ancestral y energías kármicas. Si deseas conocer el propósito que marcan tus números, entender los patrones de tu pasado, o protegerte de energías negativas... puedo ayudarte a interpretarlo. ¿Te gustaría que exploremos juntos tu camino numérico?`;
          break;
        case 'samael':
        default:
          welcomeMessageContent = `¡Hola! Soy Maestro Samael, experto en astrología cuántica y tarot de sueños. Si sientes curiosidad por la energía que te rodea en este momento, has tenido un sueño repetitivo que quisieras comprender, o deseas claridad sobre una situación específica que estás viviendo, estoy aquí para guiarte. ¿Sobre qué te gustaría que conversemos hoy?`;
          break;
      }
      
      const welcomeMessage: Message = {
        id: `welcome_${Date.now()}`,
        content: welcomeMessageContent,
        type: 'text',
        sender: 'bot',
        timestamp: new Date(),
        status: 'sent',
        isPartial: false
      };
      setMessages([welcomeMessage]);
    }
  }, [isGuestMode, historyChecked, messages.length, isBotTyping, master, personality]);

  // Socket event listeners
  useEffect(() => {
    if (!socket) {
      // Socket no disponible aún, reintentar en el siguiente ciclo
      return;
    }

    const handleMessage = (message: any) => {
      // Solo log de errores importantes
      
      const messageContent = message.text || message.content;
      
      setMessages(prev => {
        // Cada bloque es un mensaje separado con su propio ID único
        const newMessage: Message = {
          id: message.id, // Usar el ID único del bloque (messageId_blockIndex)
          content: messageContent,
          type: message.type || 'text',
          sender: message.sender || 'bot',
          timestamp: new Date(message.timestamp),
          status: 'sent',
          isPartial: message.isPartial,
          blockIndex: message.blockIndex,
          totalBlocks: message.totalBlocks,
          messageId: message.messageId, // Referencia al mensaje original
        };
        
        // Creando mensaje de bloque
        return [...prev, newMessage];
      });
      
      // Desactivar spinner cuando llega respuesta del bot
      if (message.sender === 'bot' || !message.sender) {
        stopTypingIndicator();
        setCommunicationError(false);
        // Limpiar timeout si existe
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
        }
        // Cancelar delay de 1.5s si existe
        if ((typingTimeoutRef as any).typingDelayTimeout) {
          clearTimeout((typingTimeoutRef as any).typingDelayTimeout);
          (typingTimeoutRef as any).typingDelayTimeout = null;
        }
      }
    };

    const handleTyping = (data: any) => {
      if (data.userId === 'bot') {
        // Si el backend envía evento typing, cancelar el delay de 1.5s y activar spinner inmediatamente
        if (data.isTyping) {
          if ((typingTimeoutRef as any).typingDelayTimeout) {
            clearTimeout((typingTimeoutRef as any).typingDelayTimeout);
            (typingTimeoutRef as any).typingDelayTimeout = null;
          }
          setIsBotTyping(true);
        } else {
          stopTypingIndicator();
        }
        
        // Si el backend dice que sigue escribiendo, cancelar timeout y limpiar error
        if (data.isTyping) {
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = null;
          }
          setCommunicationError(false);
          
          // Cuando el bot empieza a escribir, marcar el último mensaje del usuario como 'sent'
          setMessages(prev => {
            // Encontrar el último mensaje del usuario que esté en estado 'sending'
            // Buscar desde el final del array hacia atrás
            let lastSendingIndex = -1;
            for (let i = prev.length - 1; i >= 0; i--) {
              if (prev[i].sender === 'user' && prev[i].status === 'sending') {
                lastSendingIndex = i;
                break;
              }
            }
            
            if (lastSendingIndex !== -1) {
              const updated = [...prev];
              updated[lastSendingIndex] = {
                ...updated[lastSendingIndex],
                status: 'sent'
              };
              // Mensaje del usuario marcado como enviado
              return updated;
            }
            return prev;
          });
        } else {
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = null;
          }
          setCommunicationError(false);
        }
      } else {
        // Evento typing no es del bot
      }
    };

    const handleError = (error: any) => {
      console.error('Error de WebSocket:', error.message || 'Error de conexión');
      // No mostrar error al usuario, solo logear
    };

    const handleSubscriptionStatus = (data: any) => {
      const reason = data?.reason || '';
      const premiumActive = reason
        ? reason.toLowerCase().includes('suscripción válida')
        : Boolean(data?.isPremium);
      setIsPremium(premiumActive);
    };

    socket.on('message', handleMessage);
    socket.on('typing', handleTyping);
    socket.on('error', handleError);
    socket.on('subscription_status', handleSubscriptionStatus);

    return () => {
      socket.off('message', handleMessage);
      socket.off('typing', handleTyping);
      socket.off('error', handleError);
      socket.off('subscription_status', handleSubscriptionStatus);
    };
  }, [socket, stopTypingIndicator]);

  const sendMessage = async (content: string, type: 'text' | 'audio' | 'image' = 'text') => {
    if (!content.trim() || !user) return;

    const messageId = Date.now().toString();
    const newMessage: Message = {
      id: messageId,
      content,
      type,
      sender: 'user',
      timestamp: new Date(),
      status: 'sending',
    };

    setMessages(prev => {
      const updated = [...prev, newMessage];
      messagesRef.current = updated;
      return updated;
    });
    setInputValue('');
    setError(null);
    setCommunicationError(false);
    clearPremiumTypingTimers();
    clearPremiumTypingTimers();
    
    // Limpiar timeout anterior si existe
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    if ((typingTimeoutRef as any).typingDelayTimeout) {
      clearTimeout((typingTimeoutRef as any).typingDelayTimeout);
      (typingTimeoutRef as any).typingDelayTimeout = null;
    }

    startTypingIndicators();

    try {
      let endpoint = `${config.API_URL}/api/chat/send`;
      let formData: any = {
        content,
        type,
        userId: user.id,
        master,
      };

      if (type === 'audio') {
        endpoint = '/api/chat/audio';
        // Audio will be handled by AudioRecorder component
        return;
      }

      // Enviar petición al servidor
      // El mensaje se quedará en 'sending' hasta que el bot empiece a escribir
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 segundos timeout
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify(formData),
        signal: controller.signal,
        cache: 'no-store'
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403 && errorData.error === 'subscription_required') {
          handleSubscriptionRestriction(errorData.message);
          return;
        }

        if (response.status >= 500) {
          setMessages(prev => 
            prev.map(msg => 
              msg.id === messageId 
                ? { ...msg, status: 'error' }
                : msg
            )
          );
          throw new Error(errorData.message || 'Error del servidor al enviar mensaje');
        } else {
          stopTypingIndicator();
          clearPremiumTypingTimers();
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = null;
          }
          if ((typingTimeoutRef as any).typingDelayTimeout) {
            clearTimeout((typingTimeoutRef as any).typingDelayTimeout);
            (typingTimeoutRef as any).typingDelayTimeout = null;
          }
          return;
        }
      }

      // Timeout para marcar como enviado si no hay respuesta en 30 segundos
      setTimeout(() => {
        setMessages(prev => 
          prev.map(msg => 
            msg.id === messageId && msg.status === 'sending'
              ? { ...msg, status: 'sent' }
              : msg
          )
        );
      }, 30000);

    } catch (error: any) {
      // Solo logear errores críticos del servidor
      if (error.name === 'AbortError') {
        // Timeout - el mensaje puede haberse enviado via WebSocket
      } else if (error.response?.status >= 500) {
        console.error('Error crítico del servidor:', error.message);
        setMessages(prev => 
          prev.map(msg => 
            msg.id === messageId 
              ? { ...msg, status: 'error' }
              : msg
          )
        );
      }
      // Para otros errores, el WebSocket puede haber procesado el mensaje correctamente
      stopTypingIndicator();
      clearPremiumTypingTimers();
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      if ((typingTimeoutRef as any).typingDelayTimeout) {
        clearTimeout((typingTimeoutRef as any).typingDelayTimeout);
        (typingTimeoutRef as any).typingDelayTimeout = null;
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const handleAudioRecording = async (audioBlob: Blob) => {
    if (!user) return;

    // Usar el tipo MIME real del Blob y determinar extensión correcta
    const blobType = audioBlob.type || 'audio/webm';
    let filename = 'recording.webm';
    
    if (blobType.includes('webm')) {
      filename = 'recording.webm';
    } else if (blobType.includes('ogg')) {
      filename = 'recording.ogg';
    } else if (blobType.includes('wav')) {
      filename = 'recording.wav';
    } else if (blobType.includes('mp4') || blobType.includes('m4a')) {
      filename = 'recording.m4a';
    }
    

    // Convertir Blob a File usando el tipo real
    const audioFile = new File([audioBlob], filename, { type: blobType });
    await handleFileUpload(audioFile, 'audio');
  };

  const handleFileUpload = async (file: File, type: 'audio') => {
    if (!user) return;

    const formData = new FormData();
    formData.append(type, file);
    formData.append('userId', user.id.toString());
    formData.append('master', master);

    const messageId = Date.now().toString();
    const newMessage: Message = {
      id: messageId,
      content: '🎤 Mensaje de voz',
      type,
      sender: 'user',
      timestamp: new Date(),
      status: 'sending',
    };

    setMessages(prev => {
      const updated = [...prev, newMessage];
      messagesRef.current = updated;
      return updated;
    });
    setError(null);
    setCommunicationError(false);
    
    // Limpiar timeout anterior si existe
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    if ((typingTimeoutRef as any).typingDelayTimeout) {
      clearTimeout((typingTimeoutRef as any).typingDelayTimeout);
      (typingTimeoutRef as any).typingDelayTimeout = null;
    }

    startTypingIndicators();

    try {
      const endpoint = '/api/chat/audio';
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 segundos timeout
      
      const response = await fetch(`${config.API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: formData,
        signal: controller.signal,
        cache: 'no-store'
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 403) {
          const errorData = await response.json().catch(() => ({}));
          if (errorData.error === 'subscription_required') {
            handleSubscriptionRestriction(errorData.message);
            return;
          }
        }

        if (response.status >= 500) {
          setMessages(prev => 
            prev.map(msg => 
              msg.id === messageId 
                ? { ...msg, status: 'error' }
                : msg
            )
          );
          throw new Error('Error al procesar archivo');
        }
        stopTypingIndicator();
        clearPremiumTypingTimers();
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
        }
        if ((typingTimeoutRef as any).typingDelayTimeout) {
          clearTimeout((typingTimeoutRef as any).typingDelayTimeout);
          (typingTimeoutRef as any).typingDelayTimeout = null;
        }
        return;
      }

    } catch (error: any) {
      // Solo marcar como error si es un error crítico, no por timeout
      if (error.name !== 'AbortError' && error.response?.status >= 500) {
        console.error('Error al procesar audio:', error);
        setMessages(prev => 
          prev.map(msg => 
            msg.id === messageId 
              ? { ...msg, status: 'error' }
              : msg
          )
        );
      }
      // Para timeouts y errores no críticos, mantener como 'sending'
      stopTypingIndicator();
      clearPremiumTypingTimers();
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      if ((typingTimeoutRef as any).typingDelayTimeout) {
        clearTimeout((typingTimeoutRef as any).typingDelayTimeout);
        (typingTimeoutRef as any).typingDelayTimeout = null;
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Messages Area */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <MessageList 
          messages={messages} 
          isBotTyping={isBotTyping}
          user={user}
          historyChecked={historyChecked}
          personality={personality}
          accentColor={accentColor}
          isPremium={isPremium}
        />
        <div ref={messagesEndRef} />
      </Box>

      {/* Error Display */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mx: { xs: 1.5, sm: 2 }, mb: 1 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Communication Error Display */}
      {communicationError && (
        <Alert 
          severity="warning" 
          sx={{ mx: { xs: 1.5, sm: 2 }, mb: 1 }}
          onClose={() => setCommunicationError(false)}
        >
          Parece que hay problemas de comunicación en este momento.
        </Alert>
      )}

      {/* Input Area */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderTop: `1px solid rgba(255, 255, 255, 0.2)`,
          background: accentColor || '#8B5CF6',
          pb: { xs: 3, sm: 2 },
          position: 'relative',
          zIndex: 1000,
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: { xs: 0.75, sm: 1 },
              flexWrap: 'wrap',
            }}
          >
            <TextField
              multiline
              maxRows={4}
              value={inputValue}
              inputRef={inputRef}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Escribe tu mensaje..."
              variant="outlined"
              size="small"
              disabled={isBotTyping}
              sx={{
                flex: '1 1 220px',
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  color: 'white',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.7)',
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(255, 255, 255, 0.6)',
                  opacity: 1,
                },
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 0.75 }, flexShrink: 0 }}>
              <AudioRecorder
                onRecordingComplete={handleAudioRecording}
                onError={handleRecorderError}
                accentColor={accentColor}
              />
              
              <IconButton
                type="submit"
                disabled={!inputValue.trim() || isBotTyping}
                sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  color: 'white',
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    color: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        </form>

        {/* Premium Status Indicator (solo si no es guest) */}
        {!isGuestMode && (
          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.75, sm: 1 },
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 0.75 } }}>
              <IconButton
                onClick={handleNavigateHome}
                sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  color: 'white',
                  flexShrink: 0,
                  width: { xs: 40, sm: 44 },
                  height: { xs: 40, sm: 44 },
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                  },
                }}
              >
                <HomeIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={handleOpenSettings}
                sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  color: 'white',
                  flexShrink: 0,
                  width: { xs: 40, sm: 44 },
                  height: { xs: 40, sm: 44 },
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                  },
                }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={handleLogout}
                sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  color: 'white',
                  flexShrink: 0,
                  width: { xs: 40, sm: 44 },
                  height: { xs: 40, sm: 44 },
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                  },
                }}
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Box>
            {isPremium ? (
            <Box
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                borderRadius: 1,
                px: { xs: 1.25, sm: 1.5 },
                py: { xs: 0.5, sm: 0.75 },
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#F59E0B',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: { xs: '0.85rem', sm: '0.95rem' },
                }}
              >
                ⭐ Usuario Premium
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                borderRadius: 1,
                px: { xs: 1.25, sm: 1.5 },
                py: { xs: 0.5, sm: 0.75 },
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontWeight: 500,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    lineHeight: 1.4,
                    flexWrap: 'wrap',
                  }}
                >
                  Adquiere la membresía: 
                  <a 
                    href="https://mystictarot.co/pay/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      color: accentColor || '#8B5CF6', 
                      textDecoration: 'none',
                      fontWeight: 600
                    }}
                  >
                    https://mystictarot.co/pay/
                  </a>
                </Typography>
                {personality?.pricing && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      display: 'flex',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span>Semanal: ${personality.pricing.weekly.toLocaleString('es-CO')}</span>
                    <span>•</span>
                    <span>Mensual: ${personality.pricing.monthly.toLocaleString('es-CO')}</span>
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}

      </Box>

      {/* Modal de Configuración */}
      <Modal
        open={settingsModalOpen}
        onClose={handleCloseSettings}
        aria-labelledby="settings-modal-title"
        aria-describedby="settings-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 500 },
            maxWidth: 500,
            bgcolor: 'rgba(26, 26, 46, 0.98)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: 2,
            boxShadow: 24,
            p: { xs: 3, sm: 4 },
            outline: 'none',
          }}
        >
          <Typography
            id="settings-modal-title"
            variant="h6"
            component="h2"
            sx={{
              color: 'white',
              mb: 3,
              fontWeight: 600,
            }}
          >
            Editar Perfil
          </Typography>

          {settingsSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Los datos fueron actualizados correctamente
            </Alert>
          )}

          {settingsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {settingsError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Nombre"
            value={settingsName}
            onChange={(e) => setSettingsName(e.target.value)}
            margin="normal"
            required
            disabled={settingsLoading}
            sx={{
              mb: 2,
              '& label': { color: 'rgba(255, 255, 255, 0.7)' },
              '& label.Mui-focused': { color: '#f08b55' },
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                '&.Mui-focused fieldset': { borderColor: '#f08b55' },
              },
            }}
          />

          <TextField
            fullWidth
            label="Nueva Contraseña"
            type={showSettingsPassword ? 'text' : 'password'}
            value={settingsPassword}
            onChange={(e) => setSettingsPassword(e.target.value)}
            margin="normal"
            disabled={settingsLoading}
            helperText="Dejar vacío para no cambiar la contraseña"
            sx={{
              mb: 2,
              '& label': { color: 'rgba(255, 255, 255, 0.7)' },
              '& label.Mui-focused': { color: '#f08b55' },
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                '&.Mui-focused fieldset': { borderColor: '#f08b55' },
              },
              '& .MuiFormHelperText-root': {
                color: 'rgba(255, 255, 255, 0.5)',
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowSettingsPassword(!showSettingsPassword)}
                    edge="end"
                    disabled={settingsLoading}
                    sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  >
                    {showSettingsPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {settingsPassword && (
            <TextField
              fullWidth
              label="Confirmar Nueva Contraseña"
              type={showSettingsConfirmPassword ? 'text' : 'password'}
              value={settingsConfirmPassword}
              onChange={(e) => setSettingsConfirmPassword(e.target.value)}
              margin="normal"
              disabled={settingsLoading}
              sx={{
                mb: 2,
                '& label': { color: 'rgba(255, 255, 255, 0.7)' },
                '& label.Mui-focused': { color: '#f08b55' },
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: '#f08b55' },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowSettingsConfirmPassword(!showSettingsConfirmPassword)}
                      edge="end"
                      disabled={settingsLoading}
                      sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                    >
                      {showSettingsConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
            <Button
              onClick={handleCloseSettings}
              disabled={settingsLoading}
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUpdateProfile}
              disabled={settingsLoading}
              variant="contained"
              sx={{
                backgroundColor: '#f08b55',
                '&:hover': {
                  backgroundColor: '#d97947',
                },
                '&:disabled': {
                  backgroundColor: '#f0ab85',
                },
              }}
            >
              {settingsLoading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'Actualizar'
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ChatInterface;
